import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Send, X, Search, Users, MapPin, FileText } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";

interface Partner {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
  trade?: string;
  profileImageUrl?: string;
}

interface ResendReferralModalProps {
  referral: {
    id: number;
    senderId: string;
    receiverId: string;
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
    jobDescription?: string;
    jobLocation?: string;
    referralAttachments?: string | null;
    trades?: string[];
    customTrades?: string[];
  };
  isOpen: boolean;
  onClose: () => void;
}

export default function ResendReferralModal({
  referral,
  isOpen,
  onClose,
}: ResendReferralModalProps) {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");

  const { data: connections = [], isPending: connectionsLoading } = useQuery({
    queryKey: ["/api/workflows/connections"],
    queryFn: () => api.get("/api/workflows/connections"),
    enabled: !!user && isOpen,
  });

  const partnersArray: Partner[] = useMemo(
    () => (Array.isArray(connections) ? (connections as Partner[]) : []),
    [connections]
  );

  // Exclude the original receiver so the sender can't resend to the same partner.
  const availablePartners = useMemo(
    () => partnersArray.filter((p) => p.id !== referral.receiverId),
    [partnersArray, referral.receiverId]
  );

  const filteredPartners = useMemo(() => {
    if (!search.trim()) return availablePartners;
    const q = search.toLowerCase();
    return availablePartners.filter((p) => {
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      const company = p.company?.toLowerCase() || "";
      const trade = p.trade?.toLowerCase() || "";
      return fullName.includes(q) || company.includes(q) || trade.includes(q);
    });
  }, [availablePartners, search]);

  const selectedPartner = useMemo(
    () => availablePartners.find((p) => p.id === selectedPartnerId) || null,
    [availablePartners, selectedPartnerId]
  );

  const parseAttachments = (raw: any) => {
    if (!raw) return undefined;
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : undefined;
    } catch {
      return undefined;
    }
  };

  const resendMutation = useMutation({
    mutationFn: async (receiverId: string) => {
      return api.post("/api/workflows/referral/create", {
        receiverId,
        clientName: referral.clientName || "",
        clientEmail: referral.clientEmail || undefined,
        clientPhone: referral.clientPhone || undefined,
        jobDescription: referral.jobDescription || "",
        jobLocation: referral.jobLocation || "",
        attachments: parseAttachments(referral.referralAttachments),
        trades: referral.trades && referral.trades.length > 0 ? referral.trades : undefined,
        customTrades:
          referral.customTrades && referral.customTrades.length > 0
            ? referral.customTrades
            : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      showSuccess(
        "Referral Sent",
        selectedPartner
          ? `Sent to ${selectedPartner.firstName} ${selectedPartner.lastName}.`
          : "A new referral has been sent."
      );
      setSelectedPartnerId("");
      setSearch("");
      onClose();
    },
    onError: (error: any) => {
      showError(
        "Couldn't Send Referral",
        error?.message || "Failed to resend the referral. Please try again."
      );
    },
  });

  const handleClose = () => {
    if (resendMutation.isPending) return;
    setSelectedPartnerId("");
    setSearch("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-lg bg-white/95 backdrop-blur-lg border-0 shadow-xl p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col">
        <DialogHeader className="border-b border-blue-100 pb-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
          <DialogTitle className="flex items-center gap-2 text-gray-900 tracking-tight font-bold text-xl">
            <Send className="w-6 h-6 text-blue-600" />
            Resend Referral
          </DialogTitle>
          <DialogDescription className="text-gray-700 mt-1">
            Forward this referral to a different partner. The original declined referral will not
            be changed.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Read-only summary of what's being forwarded */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
              Forwarding
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="font-semibold text-gray-900">
                {referral.clientName || "Unnamed client"}
              </span>
            </div>
            {referral.jobLocation && (
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <span>{referral.jobLocation}</span>
              </div>
            )}
            {referral.jobDescription && (
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-3">{referral.jobDescription}</span>
              </div>
            )}
          </div>

          {/* Partner picker */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-gray-900">Send to</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search partners by name, company, or trade"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {connectionsLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">Loading partners...</div>
              ) : filteredPartners.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  {availablePartners.length === 0
                    ? "No other partners available."
                    : "No partners match your search."}
                </div>
              ) : (
                filteredPartners.map((partner) => {
                  const isSelected = partner.id === selectedPartnerId;
                  return (
                    <button
                      key={partner.id}
                      type="button"
                      onClick={() => setSelectedPartnerId(partner.id)}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                        isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage
                          src={partner.profileImageUrl || undefined}
                          alt={`${partner.firstName} ${partner.lastName}`}
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                          {partner.firstName?.[0]}
                          {partner.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {partner.firstName} {partner.lastName}
                        </p>
                        {partner.company && (
                          <p className="text-xs text-gray-600 truncate">{partner.company}</p>
                        )}
                      </div>
                      {partner.trade && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                          {partner.trade}
                        </Badge>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-200 bg-white">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={resendMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button
            onClick={() => selectedPartnerId && resendMutation.mutate(selectedPartnerId)}
            disabled={!selectedPartnerId || resendMutation.isPending}
            className={`flex-1 flex items-center justify-center gap-2 font-medium ${
              selectedPartnerId
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {resendMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Referral
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
